import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { financeApi as F, inr } from "@/lib/finance";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer } from "lucide-react";

const HOME = {
  name: "Nayara Digital Prints",
  address: "Surat, Gujarat 395010",
  gstin: "24ABCDE1234F1Z5",
  state: "Gujarat",
};

export default function InvoicePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);

  useEffect(() => { F.getInvoice(id).then(setInv).catch(() => { toast.error("Not found"); navigate("/finance"); }); }, [id, navigate]);
  if (!inv) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  const t = inv.totals || {};
  const a = inv.customer?.billing_address || {};

  return (
    <div className="mx-auto max-w-3xl" data-testid="invoice-print-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>
      <div className="no-print mb-4 flex items-center justify-between">
        <button onClick={() => navigate(`/finance/invoices/${id}`)} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back</button>
        <Button onClick={() => window.print()} data-testid="do-print-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Printer className="mr-2 h-4 w-4" />Print / Save PDF</Button>
      </div>

      <div className="rounded-lg border border-slate-300 bg-white p-8 text-sm text-slate-800">
        <div className="flex items-start justify-between border-b border-slate-200 pb-4">
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900">{HOME.name}</h1>
            <p className="text-slate-500">{HOME.address}</p>
            <p className="text-slate-500">GSTIN: {HOME.gstin}</p>
          </div>
          <div className="text-right">
            <p className="font-heading text-lg font-bold uppercase tracking-wide text-indigo-700">Tax Invoice</p>
            <p className="text-slate-700">{inv.invoice_no}</p>
            <p className="text-slate-500">Date: {inv.invoice_date}</p>
            <p className="text-slate-500">Due: {inv.due_date}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase text-slate-400">Bill to</p>
            <p className="font-medium text-slate-900">{inv.customer?.company_name}</p>
            <p className="text-slate-500">{[a.line1, a.city, a.state, a.pincode].filter(Boolean).join(", ")}</p>
            <p className="text-slate-500">GSTIN: {inv.customer?.gstin || "—"}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase text-slate-400">Reference</p>
            <p className="text-slate-600">SO: {inv.so_no}</p>
            <p className="text-slate-600">Challans: {(inv.challan_nos || []).join(", ")}</p>
            <p className="text-slate-600">Place of supply: {inv.place_of_supply || "—"}</p>
          </div>
        </div>

        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-y border-slate-300 bg-slate-50 text-left text-xs uppercase text-slate-500">
              <th className="py-2 pl-2">Description</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Rate</th><th className="py-2 text-right">Taxable</th><th className="py-2 text-right">GST%</th><th className="py-2 pr-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(inv.items || []).map((it) => (
              <tr key={it.id} className="border-b border-slate-100">
                <td className="py-2 pl-2">{it.description}</td>
                <td className="py-2 text-right tabular-nums">{it.quantity} {it.unit}</td>
                <td className="py-2 text-right tabular-nums">{inr(it.rate)}</td>
                <td className="py-2 text-right tabular-nums">{inr(it.taxable)}</td>
                <td className="py-2 text-right tabular-nums">{it.tax_pct}%</td>
                <td className="py-2 pr-2 text-right tabular-nums">{inr(it.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <dl className="w-64 space-y-1">
            <PL l="Taxable value" v={inr(t.taxable_value)} />
            {inv.tax_mode === "intra" ? (<><PL l="CGST" v={inr(t.cgst)} /><PL l="SGST" v={inr(t.sgst)} /></>) : <PL l="IGST" v={inr(t.igst)} />}
            {t.tcs > 0 && <PL l="TCS" v={inr(t.tcs)} />}
            {t.round_off !== 0 && <PL l="Round off" v={inr(t.round_off)} />}
            <div className="flex justify-between border-t border-slate-300 pt-1 font-bold text-slate-900"><dt>Grand total</dt><dd className="tabular-nums">{inr(t.grand_total)}</dd></div>
            <PL l="Balance due" v={inr(inv.balance_amount)} />
          </dl>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-4 text-xs text-slate-500">
          <p>{inv.terms || "Goods once sold will not be taken back. Subject to Surat jurisdiction."}</p>
          <p className="mt-6 text-right">For {HOME.name}</p>
          <p className="mt-8 text-right">Authorised Signatory</p>
        </div>
      </div>
    </div>
  );
}

const PL = ({ l, v }) => (<div className="flex justify-between"><dt className="text-slate-500">{l}</dt><dd className="tabular-nums text-slate-700">{v}</dd></div>);
